import { cn } from '@noor/lib/cn';

export interface FolderTabItem<T extends string = string> {
  value: T;
  label: string;
}

export interface FolderTabsProps<T extends string = string> {
  items: readonly FolderTabItem<T>[];
  value: T;
  onChange: (val: T) => void;
  className?: string;
}

/**
 * Folder-style tab bar for modal panels (Figma Noor-CPanel 1100:24884): a
 * solid brand-orange track with `rounded-t-[12px]` corners; the active tab is
 * a white folder tab (`rounded-t-[8px]`, 14px-medium black label) docked to
 * the content panel below, idle tabs are 14px-regular white labels.
 *
 * The bar owns only itself — render the matching panel (`rounded-b-[12px]`
 * hairline border) directly underneath with no gap. Horizontally scrollable
 * with a hidden scrollbar so narrow viewports keep every tab reachable.
 */
export function FolderTabs<T extends string = string>({
  items,
  value,
  onChange,
  className,
}: FolderTabsProps<T>) {
  return (
    <div
      role="tablist"
      aria-orientation="horizontal"
      className={cn(
        'flex h-[45px] max-w-full items-stretch gap-0 overflow-x-auto rounded-t-[12px] bg-primary pt-1 pl-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        className,
      )}
    >
      <div className="flex items-center">
        {items.map((tab) => {
          const isActive = tab.value === value;
          return (
            <button
              key={tab.value}
              role="tab"
              type="button"
              aria-selected={isActive}
              tabIndex={isActive ? 0 : -1}
              onClick={() => onChange(tab.value)}
              onKeyDown={(event) => {
                if (event.key !== 'ArrowRight' && event.key !== 'ArrowLeft') return;
                event.preventDefault();
                const index = items.findIndex((item) => item.value === tab.value);
                const delta = event.key === 'ArrowRight' ? 1 : -1;
                const next = items[(index + delta + items.length) % items.length];
                if (next) {
                  onChange(next.value);
                  (
                    event.currentTarget.parentElement?.children[items.indexOf(next)] as
                      | HTMLElement
                      | undefined
                  )?.focus();
                }
              }}
              className={cn(
                'inline-flex shrink-0 cursor-pointer items-center justify-center gap-1 px-4 text-sm tracking-[-0.28px] whitespace-nowrap transition-colors outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-inset',
                isActive
                  ? 'h-[41px] self-end rounded-t-[8px] bg-white font-medium text-black'
                  : 'h-8 rounded-[8px] font-normal text-white hover:bg-white/10',
              )}
            >
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}
